import axios from "axios";
import PropTypes from "prop-types";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import API from "../api";

// Create the Context
const AuthenticationContext = createContext();

const AuthenticationProvider = ({ children }) => {
  // Stores the logged in user
  const [user, setUser] = useState({});

  // Indicates that the side is loading and auto-login is performed
  const [loading, setLoading] = useState(true);
  const hasRun = useRef(false);

  const handleRefreshToken = async () => {
    const data = await API.refreshToken();
    if (
      data.status === true &&
      data.data?.access_token &&
      data.data?.user.is_admin === true
    ) {
      // Adds JWT-Token to every Axios-request
      axios.defaults.headers.common.Authorization = `Bearer ${data.data.access_token}`;

      setUser(data.data.user);

      // JWT-Token is 60 minutes valid, refresh after 30 Minutes
      setTimeout(() => {
        handleRefreshToken();
      }, 30 * 60 * 1000);
    } else {
      await API.logout();

      // Remove JWT-Token from Axios-Header
      axios.defaults.headers.common.Authorization = null;

      // clear User
      setUser({});
    }
    setLoading(false);

    // return data.success;
    return null;
  };

  // Auto-Login on startup
  useEffect(() => {
    // Add language header
    if (hasRun.current) return;
    hasRun.current = true;
    handleRefreshToken();
    // eslint-disable-next-line
  }, []);

  const login = async (username, password, stayLoggedIn = false) => {
    console.log("Login called");

    const result = await API.login(username, password);
    if (result.status === true) {
      console.log("RESULT", result);

      // Check if user is an admin
      if (result.data.user.is_admin !== true) {
        await API.logout();
        return false;
      }

      // Add JWT-Token to every Axios-Request
      axios.defaults.headers.common.Authorization = `Bearer ${result.data.access_token}`;

      // Set user
      setUser(result.data.user);

      return true;
    }
    return false;
  };

  const logout = async () => {
    console.log("Logout called");

    // Removes and invalidates the refresh-Token cookie
    await API.logout();

    // Remove JWT-Token from Axios-Header
    axios.defaults.headers.common.Authorization = null;

    // clear User
    setUser({});
  };

  const isAuthenticated = () => {
    if (Object.entries(user).length === 0) {
      return false;
    }
    return true;
  };

  const hasRole = (role) => {
    if (Object.entries(user).length === 0) {
      return false;
    }
    if (user.roles.length === 0) {
      return false;
    }
    return user.roles.includes(role);
  };

  const hasAnyRoles = (roles) => {
    if (Object.entries(user).length === 0) {
      return false;
    }
    if (user.roles.length === 0) {
      return false;
    }

    if (roles == null || roles.length === 0) {
      return false;
    }

    // Filtere alle Rollen raus die mit ! beginnen
    const excludingRoles = roles.filter((r) => r.startsWith("!"));

    if (excludingRoles != null && excludingRoles.length > 0) {
      // Prüfen ob der User einer dieser Rolle hat. Wenn ja, false zurückgeben

      for (let i = 0; i < excludingRoles.length; i += 1) {
        for (let x = 0; x < user.roles.length; x += 1) {
          if (excludingRoles[i].substring(1) === user.roles[x]) {
            return false;
          }
        }
      }

      return true;
    }

    return user.roles.filter((userrole) => roles.includes(userrole)).length > 0;
  };

  // All values and functions that are passed to the context
  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      isAuthenticated,
      hasRole,
      hasAnyRoles,
    }),
    // eslint-disable-next-line
    [user]
  );

  return (
    <AuthenticationContext.Provider value={value}>
      {loading ? <p>Wird geladen...</p> : children}
    </AuthenticationContext.Provider>
  );
};

AuthenticationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
export { AuthenticationProvider };

export default function useAuth() {
  return useContext(AuthenticationContext);
}
